import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import axios from 'axios';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { CONFIG } from '../utils/config';
import { blogsMetadata as fallbackBlogs } from '../data/blogsMetadata';
import { parseFrontmatter } from './Blog';
import LoadingSkeleton from '../components/LoadingSkeleton';

const BlogPost = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [blogMeta, setBlogMeta] = useState(null);
    const [content, setContent] = useState('');
    const [headings, setHeadings] = useState([]);
    const [loading, setLoading] = useState(true);

    const extractHeadings = (markdown) => {
        const headingRegex = /^(#{2,3})\s+(.+)$/gm;
        const foundHeadings = [];
        let match;
        while ((match = headingRegex.exec(markdown)) !== null) {
            let headingText = match[2].trim();
            // Remove markdown links but keep text: [text](url) -> text
            headingText = headingText.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
            // Remove bold/italic markers and backticks
            headingText = headingText.replace(/[\*_`]+/g, '');
            
            // Very simple slugifier that matches rehype-slug for basic English text
            const id = headingText
                .toLowerCase()
                .trim()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/(^-|-$)/g, '');
                
            foundHeadings.push({
                level: match[1].length,
                text: headingText,
                id: id
            });
        }
        return foundHeadings;
    };

    const extractFAQs = (markdown) => {
        const faqSectionRegex = /## Frequently Asked Questions([\s\S]*?)---/i;
        let match = faqSectionRegex.exec(markdown);
        if (!match) {
            const faqSectionEof = /## Frequently Asked Questions([\s\S]*)$/i;
            match = faqSectionEof.exec(markdown);
        }
        if (!match) return [];

        const faqText = match[1];
        const faqRegex = /###\s+(.+)\n+([\s\S]*?)(?=\n###|\n##|$)/g;
        const faqs = [];
        let faqMatch;
        while ((faqMatch = faqRegex.exec(faqText)) !== null) {
            faqs.push({
                question: faqMatch[1].trim(),
                answer: faqMatch[2].trim()
            });
        }
        return faqs;
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        AOS.init({ duration: 800, once: true });

        const fetchPost = async () => {
            let targetBlog = null;
            try {
                // Try fetching from DB first
                const res = await axios.get(`${CONFIG.API_BASE}/blogs`);
                if (res.data && res.data.length > 0) {
                    for (const b of res.data) {
                        const { data, content: mdContent } = parseFrontmatter(b.content || "");
                        const mappedSlug = data.slug || b.id;
                        if (mappedSlug === slug) {
                            targetBlog = {
                                id: b.id,
                                title: data.title || b.title,
                                slug: mappedSlug,
                                category: data.category || b.category || "Update",
                                readTime: data.read_time || "3 min",
                                image: data.cover_image || "/assets/blog/default_cover.webp",
                                excerpt: data.excerpt || b.content?.substring(0, 100) + '...',
                                author: data.author || b.author || "Doumene M. Rosvel",
                                date: data.date || b.created_at
                            };
                            setContent(mdContent);
                            setHeadings(extractHeadings(mdContent));
                            break;
                        }
                    }
                }
            } catch (err) {
                console.warn("API fetch failed, falling back to static files:", err.message);
            }

            // Fallback to static files if not in DB or API failed
            if (!targetBlog) {
                const staticMeta = fallbackBlogs.find(b => b.slug === slug);
                if (staticMeta) {
                    targetBlog = staticMeta;
                    try {
                        const fileRes = await fetch(`/blogs/${slug}.md`);
                        if (fileRes.ok) {
                            const text = await fileRes.text();
                            const { content: mdContent } = parseFrontmatter(text);
                            setContent(mdContent);
                            setHeadings(extractHeadings(mdContent));
                        }
                    } catch (e) {
                        console.error("Failed to load static markdown", e);
                    }
                }
            }
            
            if (targetBlog) {
                setBlogMeta(targetBlog);
            } else {
                navigate('/blog');
            }
            setLoading(false);
        };

        fetchPost();
    }, [slug, navigate]);

    if (loading || !blogMeta) {
        return (
            <LoadingSkeleton text={!blogMeta ? "Finding article..." : "Loading content..."} />
        );
    }

    const currentUrl = window.location.href;
    const faqs = extractFAQs(content);

    const articleSchema = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": blogMeta.title,
        "image": [
            blogMeta.image.startsWith('http') ? blogMeta.image : `${window.location.origin}${blogMeta.image}`
        ],
        "datePublished": blogMeta.date,
        "author": [{
            "@type": "Person",
            "name": blogMeta.author,
            "url": "https://zaptopay.online/"
        }]
    };

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [{
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://zaptopay.online/"
        },{
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": "https://zaptopay.online/blog"
        },{
            "@type": "ListItem",
            "position": 3,
            "name": blogMeta.title,
            "item": currentUrl
        }]
    };

    let faqSchema = null;
    if (faqs.length > 0) {
        faqSchema = {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.answer
                }
            }))
        };
    }

    return (
        <article className="pt-28 md:pt-48 pb-24 min-h-screen relative">
            <Helmet>
                <title>{blogMeta.title} | Zaptopay</title>
                <meta name="description" content={blogMeta.excerpt} />
                <meta property="og:title" content={blogMeta.title} />
                <meta property="og:description" content={blogMeta.excerpt} />
                <meta property="og:image" content={blogMeta.image.startsWith('http') ? blogMeta.image : `${window.location.origin}${blogMeta.image}`} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={currentUrl} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={blogMeta.title} />
                <meta name="twitter:description" content={blogMeta.excerpt} />
                <meta name="twitter:image" content={blogMeta.image.startsWith('http') ? blogMeta.image : `${window.location.origin}${blogMeta.image}`} />
                <link rel="canonical" href={currentUrl} />
                <script type="application/ld+json">
                    {JSON.stringify(articleSchema)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbSchema)}
                </script>
                {faqSchema && (
                    <script type="application/ld+json">
                        {JSON.stringify(faqSchema)}
                    </script>
                )}
            </Helmet>

            <div className="bg-blob" style={{ top: '0%', right: '10%', opacity: 0.15 }}></div>

            <div className="container max-w-6xl mx-auto relative z-10 px-4">
                
                <Link to="/blog" className="inline-flex items-center gap-2 text-primary hover:text-white transition-colors mb-12 font-bold uppercase tracking-wider text-sm">
                    ← Back to all posts
                </Link>

                <header className="mb-12" data-aos="fade-up">
                    <div className="flex items-center gap-4 mb-6 font-mono text-xs uppercase tracking-widest text-primary">
                        <span>{blogMeta.category}</span>
                        <span className="w-1 h-1 rounded-full bg-primary"></span>
                        <span>{blogMeta.readTime}</span>
                    </div>
                    
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-8 leading-tight text-text-main">
                        {blogMeta.title}
                    </h1>
                    
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-primary to-secondary flex items-center justify-center shadow-[0_0_15px_rgba(0,255,136,0.5)] border-2 border-primary/20">
                            <span className="text-black font-bold text-lg">DR</span>
                        </div>
                        <div>
                            <div className="font-bold text-text-main">{blogMeta.author}</div>
                            <div className="text-sm text-text-muted">
                                {new Date(blogMeta.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                            </div>
                        </div>
                    </div>
                </header>

                {/* Hero Image Banner */}
                <div className="w-full overflow-hidden rounded-2xl mb-12 border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5)] bg-black/40" data-aos="zoom-in">
                    <img 
                        src={blogMeta.image} 
                        alt={blogMeta.title} 
                        className="w-full h-auto object-contain aspect-[1200/630]"
                     loading="lazy" />
                </div>

                <div className="flex flex-col lg:flex-row gap-12">
                    {/* Sticky Table of Contents Sidebar */}
                    {headings.length > 0 && (
                        <aside className="lg:w-1/4 hidden lg:block relative">
                            <div className="sticky top-32 glass p-6 rounded-xl border border-primary/20 shadow-[0_0_20px_rgba(0,255,136,0.1)]">
                                <h3 className="text-primary font-black uppercase tracking-widest text-xs mb-6 flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>
                                    Table of Contents
                                </h3>
                                <ul className="space-y-4 text-sm font-medium">
                                    {headings.map((h, i) => (
                                        <li key={i} className={`${h.level === 3 ? "ml-4 text-text-muted" : "text-white/90"} transition-colors`}>
                                            <a 
                                                href={`#${h.id}`} 
                                                className="hover:text-primary transition-all duration-300 hover:translate-x-1 block"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth' });
                                                }}
                                            >
                                                {h.text}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </aside>
                    )}

                    {/* Main Content Area */}
                    <div 
                        className={`blog-content border-t lg:border-t-0 border-glass-border pt-10 lg:pt-0 ${headings.length > 0 ? 'lg:w-3/4' : 'w-full max-w-4xl mx-auto'}`}
                    >
                        {/* Mobile Table of Contents */}
                        {headings.length > 0 && (
                            <div className="lg:hidden mb-8 glass p-6 rounded-xl border border-primary/20">
                                <h3 className="text-primary font-black uppercase tracking-widest text-xs mb-4 flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>
                                    Table of Contents
                                </h3>
                                <ul className="space-y-3 text-sm font-medium">
                                    {headings.map((h, i) => (
                                        <li key={i} className={`${h.level === 3 ? "ml-4 text-text-muted" : "text-white/90"}`}>
                                            <a 
                                                href={`#${h.id}`} 
                                                className="hover:text-primary transition-colors block"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth' });
                                                }}
                                            >
                                                {h.text}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <ReactMarkdown 
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeSlug]}
                            components={{
                                a: ({node, ...props}) => {
                                    // Internal SPA links
                                    if (props.href && props.href.startsWith('/')) {
                                        return <Link to={props.href} {...props} className="text-primary hover:underline font-bold transition-colors">{props.children}</Link>;
                                    }
                                    // External links
                                    return <a target="_blank" rel="noopener noreferrer" {...props} className="text-primary hover:underline font-bold transition-colors">{props.children}</a>;
                                }
                            }}
                        >
                            {content}
                        </ReactMarkdown>
                    </div>
                </div>
                
                <div className="mt-20 pt-10 border-t border-glass-border flex justify-between items-center flex-wrap gap-4">
                    <p className="text-text-muted font-bold uppercase tracking-widest text-xs">Share this article:</p>
                    <div className="flex gap-4">
                        {/* Twitter/X */}
                        <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(blogMeta.title)}&url=${encodeURIComponent(currentUrl)}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full glass flex items-center justify-center hover:text-primary transition-colors hover:-translate-y-1" title="Share on X">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"/></svg>
                        </a>
                        {/* WhatsApp */}
                        <a href={`https://wa.me/?text=${encodeURIComponent(blogMeta.title + ' - ' + currentUrl)}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full glass flex items-center justify-center hover:text-primary transition-colors hover:-translate-y-1" title="Share on WhatsApp">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
                        </a>
                        {/* Telegram */}
                        <a href={`https://t.me/share/url?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(blogMeta.title)}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full glass flex items-center justify-center hover:text-primary transition-colors hover:-translate-y-1" title="Share on Telegram">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 10-4 4 6 6 4-16-18 7 4 2 2 6 3-4"/></svg>
                        </a>
                        {/* Reddit */}
                        <a href={`https://reddit.com/submit?url=${encodeURIComponent(currentUrl)}&title=${encodeURIComponent(blogMeta.title)}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full glass flex items-center justify-center hover:text-primary transition-colors hover:-translate-y-1" title="Share on Reddit">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                        </a>
                    </div>
                </div>

                {/* Related Posts Section */}
                <div className="mt-20 pt-10 border-t border-glass-border">
                    <h2 className="text-3xl font-black mb-8 text-white flex items-center gap-3">
                        <span className="w-8 h-1 bg-primary rounded-full"></span>
                        Related Articles
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {fallbackBlogs
                            .filter(b => b.category === blogMeta.category && b.slug !== slug)
                            .slice(0, 3)
                            .map((post, idx) => (
                                <Link 
                                    to={`/blog/${post.slug}`} 
                                    key={idx}
                                    className="group glass p-4 rounded-2xl hover:-translate-y-2 hover:shadow-[0_10px_30px_rgba(0,255,136,0.1)] transition-all duration-300 block border border-white/5 hover:border-primary/30"
                                >
                                    <div className="overflow-hidden rounded-xl mb-4 aspect-video bg-black/50">
                                        <img 
                                            src={post.image} 
                                            alt={post.title} 
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="text-primary text-xs font-bold uppercase tracking-widest mb-2">
                                        {post.category}
                                    </div>
                                    <h3 className="text-lg font-bold text-white group-hover:text-primary transition-colors line-clamp-2 mb-2">
                                        {post.title}
                                    </h3>
                                    <p className="text-sm text-text-muted line-clamp-2 mb-4">
                                        {post.excerpt}
                                    </p>
                                    <div className="text-xs font-mono text-white/50 group-hover:text-white/80 transition-colors flex justify-between items-center">
                                        <span>{new Date(post.date).toLocaleDateString()}</span>
                                        <span className="text-primary font-bold group-hover:translate-x-1 transition-transform" aria-label={`Read ${post.title}`}>Read →</span>
                                    </div>
                                </Link>
                            ))}
                    </div>
                </div>

            </div>
        </article>
    );
};

export default BlogPost;
